import { useState } from "react";
import useSWR from "swr";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ToastProvider";
import { fetcher } from "@/lib/fetcher";
import { post } from "@/lib/api";

interface ABTestPanelProps {
  availablePosts: Array<{ id: string; title: string }>;
  defaultPostId?: string;
}

export function ABTestPanel({ availablePosts, defaultPostId }: ABTestPanelProps) {
  const [postA, setPostA] = useState(defaultPostId ?? (availablePosts[0]?.id ?? ""));
  const [postB, setPostB] = useState(availablePosts[1]?.id ?? "");
  const [metric, setMetric] = useState<"engagement" | "watch_time" | "CTR">("engagement");
  const [testId, setTestId] = useState<string | null>(null);
  const [autoFinalize, setAutoFinalize] = useState(true);
  const { push } = useToast();

  const { data: testData, mutate: refreshTest } = useSWR(
    testId ? `/marketing/abtest/${testId}` : null,
    fetcher,
    { refreshInterval: testId ? 20000 : undefined },
  );

  const disabled = !postA || !postB || postA === postB;

  const runTest = async () => {
    if (disabled) {
      push({ title: "Pick two posts", message: "Select two distinct posts to compare.", variant: "destructive" });
      return;
    }
    try {
      const response = await post("/marketing/abtest/run", {
        post_id_a: postA,
        post_id_b: postB,
        metric,
      });
      if (response?.id) {
        setTestId(response.id);
        push({ title: "A/B test running", message: "We'll monitor both posts." });
      } else {
        push({ title: "Queued test", message: "Monitoring has begun." });
      }
    } catch (error) {
      push({
        title: "Unable to start test",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  const finalizeTest = async () => {
    if (!testId) return;
    try {
      await post(`/marketing/abtest/${testId}/finalize`, {
        auto_delete_loser: autoFinalize,
      });
      await refreshTest();
      push({ title: "Test finalized", message: "Winner selected" });
    } catch (error) {
      push({
        title: "Unable to finalize",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  const winner = testData?.winner;

  return (
    <Card className="space-y-3 rounded-2xl border-border/80 bg-background/40 p-4">
      <div>
        <p className="text-lg font-semibold text-foreground">A/B Test</p>
        <p className="text-xs text-muted-foreground">Compare two scheduled posts and auto-pick the winner.</p>
      </div>
      <section className="grid gap-3 md:grid-cols-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Post A
          <Select value={postA} onValueChange={(value) => setPostA(value)}>
            <SelectTrigger className="mt-1 bg-background/40 text-foreground">
              <SelectValue placeholder="Select post" />
            </SelectTrigger>
            <SelectContent className="bg-background/40 text-foreground">
              {availablePosts.map((post) => (
                <SelectItem key={post.id} value={post.id}>
                  {post.title || post.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Post B
          <Select value={postB} onValueChange={(value) => setPostB(value)}>
            <SelectTrigger className="mt-1 bg-background/40 text-foreground">
              <SelectValue placeholder="Select post" />
            </SelectTrigger>
            <SelectContent className="bg-background/40 text-foreground">
              {availablePosts.map((post) => (
                <SelectItem key={post.id} value={post.id}>
                  {post.title || post.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </section>
      <section className="grid gap-3 md:grid-cols-2">
        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Metric
          <Select value={metric} onValueChange={(value) => setMetric(value as typeof metric)}>
            <SelectTrigger className="mt-1 bg-background/40 text-foreground">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background/40 text-foreground">
              <SelectItem value="engagement">Engagement</SelectItem>
              <SelectItem value="watch_time">Watch time</SelectItem>
              <SelectItem value="CTR">Click-through</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label className="flex items-center justify-between rounded-xl border border-border/70 bg-background/40 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Auto-delete losing variant
          <Switch checked={autoFinalize} onCheckedChange={(value) => setAutoFinalize(Boolean(value))} />
        </label>
      </section>
      <div className="flex gap-2">
        <Button className="flex-1" disabled={disabled} onClick={() => void runTest()}>
          {testId ? "Re-run" : "Start A/B Test"}
        </Button>
        <Button variant="outline" disabled={!testId} onClick={() => void finalizeTest()}>
          Finalize
        </Button>
      </div>
      {winner ? (
        <Card className="border-success/60 bg-success/10 p-3 text-sm text-success">
          Winner: {winner}
        </Card>
      ) : testData ? (
        <p className="text-xs text-muted-foreground">Collecting data…</p>
      ) : null}
    </Card>
  );
}
